// How this build runs (ARCH §1.2, §12.2): the demo server in memory (default — what ships in dist/
// snapshots, REQ-001 AC1) or the backend over HTTP. Chosen once at start-up from the Vite environment;
// nothing else in the app knows which one it is. QA switches ride in the URL of demo builds only
// (TEST_PLAN §1.5): `?demoNow=<ISO instant>` pins the clock here; the rest ride as `qa`, a plain record the
// demo chunk reads with `parseDemoSwitches`.
//
// **T-076-W7.** This file used to hand back the whole unfiltered `URLSearchParams`, and the API client then
// read `window.location` a SECOND time — so every query parameter on the page reached the demo server, and a
// test that passed a synthetic location silently got the real one at the client. Both are closed here: the
// switches are filtered to the five names a demo build understands, and the client takes them off `AppConfig`
// instead of reading the address again. The names and the two fields are the phone's
// (`apps/mobile/src/foundation/config.ts`: `QA_SWITCH_NAMES`, `QaSwitches`, `qa`, `qaKey`), so the two apps
// now agree about what a switch IS; what still differs is where the address comes from — a `Location` here,
// a `Location` or a deep-link string there — which is a platform fact and not a divergence.

export type Transport = 'demo' | 'http';

/**
 * T-210 — WHICH SERVER answers on a build that has one, and why that is not a mode flag.
 *
 * `VITE_API_MODE=api` is the whole of this build's authority over its transport: it decides whether there is a
 * backend at all, and nothing in an address or in storage can give a DEMO build one. The single thing a reader
 * of an API build may choose is the opposite direction — ARCH §1.2's "an `api` build switches to
 * `DemoApiClient` when the user taps *Try the demo*", so REQ-001 AC1 ("no backend and no network required")
 * holds in a product build too.
 *
 * **That choice cannot change a mode** (REQ-100 AC4, SC-7's lying-client rows). `AccountInfo.mode` is read off
 * the wire and written by nothing here; the in-memory demo server has no network code at all, so choosing it
 * cannot widen what reaches Meta, and it answers for the SAMPLE account — which reports `demo` because it is a
 * demo, never because the address said so. The live account's mode is not a function of this value in either
 * direction: `app/t210.mode.test.tsx` is the assertion.
 */
export type ServerChoice = 'api' | 'demo';
/** `?server=api|demo` — honoured on an API build and inert on a demo build, which has no backend to pick. */
export const SERVER_PARAM = 'server';
/** Where the choice is remembered for the VISIT (sessionStorage since T-463, D-1277): a reload stays on the server
 *  you picked; a fresh visit opens the build's own default — the real account on an API build. */
export const SERVER_STORAGE_KEY = 'sl.server';

/** `api` / `demo`, or `null` for anything else — an unknown value never moves a build off its own default. */
export function parseServerChoice(raw: string | null | undefined): ServerChoice | null {
  return raw === 'api' || raw === 'demo' ? raw : null;
}

/** The QA switch names a demo build understands, in the order TEST_PLAN §1.5 lists them. */
export const QA_SWITCH_NAMES = [
  'demoNow',
  'demoScenario',
  'demoFault',
  'demoPreview',
  'demoConnection',
] as const;

export type QaSwitchName = (typeof QA_SWITCH_NAMES)[number];

/** What `parseDemoSwitches` is handed: a plain record, the same shape the phone passes it. */
export type QaSwitches = Readonly<Partial<Record<QaSwitchName, string>>>;

/**
 * T-282 (D-362) — which dataset a demo build serves: the committed demo (`demo`, the default) or the live look
 * (`live`: a dataset `tools/meta-pull.mjs` wrote from the real account, fetched from `live/` beside the app and
 * served by the demo server as a live, view-only account). Not a QA switch — it never reaches `qa` — and, like
 * them, meaningless on an `http` build, which has a real server to say what its accounts are.
 */
export type DatasetChoice = 'demo' | 'live';

/**
 * T-283 — WHICH account the shell's switch is showing: the demo, or one entry of `live/index.json`. The key is
 * the pull's opaque per-entity/account key (an HMAC, never a Meta id — HARD RULE 2), so it is safe in an
 * address; `null` means "whichever the index lists first", which is what `?dataset=live` alone has always meant.
 */
export interface DatasetSelection {
  dataset: DatasetChoice;
  liveAccountKey: string | null;
}

export const DEMO_SELECTION: DatasetSelection = { dataset: 'demo', liveAccountKey: null };

/** `?account=` — the live entry to load, when the index lists more than one. */
export const LIVE_ACCOUNT_PARAM = 'account';
/** `?dataset=` — which dataset this build serves. */
export const DATASET_PARAM = 'dataset';
/** Where the remembered choice lives, so a reload (and the next visit) keeps the account you picked. */
export const DATASET_STORAGE_KEY = 'sl.dataset';
/** An account key as the pull writes it: the same alphabet its dataset file name uses, and no traversal. */
const ACCOUNT_KEY = /^[A-Za-z0-9_-]{1,64}$/;

export interface AppConfig {
  transport: Transport;
  /**
   * T-210 — the BUILD's own answer to "is there a backend?" (`VITE_API_MODE=api`), which `transport` is not:
   * an API build showing the in-memory demo (ARCH §1.2, "Try the demo") has `apiBuild: true` and
   * `transport: 'demo'`. Every rule that belongs to the BUILD reads this — the QA switches, the live look's
   * dataset and the way back to the backend — so none of them can be turned on by an address.
   */
  apiBuild: boolean;
  /** Private owner hosting only: header login posts to the nginx password gate. */
  passwordGate?: boolean;
  /** Up to and including `/api/v1` (HttpApiClientConfig.baseUrl). */
  apiBaseUrl: string;
  /** From `?demoNow=`; demo builds only. Epoch ms, or null when absent or unparsable. */
  demoNow: number | null;
  /** From `?dataset=` (or the remembered choice); demo builds only. `demo` unless it says `live`. */
  dataset: DatasetChoice;
  /** From `?account=`; which entry of `live/index.json` to serve. `null` = the first one (T-283). */
  liveAccountKey: string | null;
  /** Every switch that was present, for the demo chunk. Empty on an `http` build (ARCH §8.3). */
  qa: QaSwitches;
  /** Changes whenever a switch does — a test (or a future deep link) can key a provider stack on it. */
  qaKey: string;
}

interface EnvLike {
  VITE_API_MODE?: string;
  VITE_API_BASE_URL?: string;
  VITE_PASSWORD_GATE?: string;
}

export const DEFAULT_API_ORIGIN = 'http://127.0.0.1:5080';
// `VITE_API_BASE_URL=/` means THE ORIGIN THAT SERVED THE PAGE: the trailing-slash trim leaves nothing, and
// `apiBaseUrl` is the bare `/api/v1`, which `fetch` resolves against the page. The box builds with it (D-1270),
// so one build answers on https://adorchestrator.com and on the IP alike — an absolute `http://` address would
// be mixed content on the https page and blocked.
export const API_PATH = '/api/v1';

/** The query part before AND inside the hash: `?demoNow=…#/home` and `#/home?demoNow=…` both count. */
export function readQaParams(
  location: Pick<Location, 'search' | 'hash'>,
): Readonly<Record<string, string>> {
  const merged = new URLSearchParams(location.search);
  const hashQuery = location.hash.indexOf('?');
  if (hashQuery >= 0) {
    for (const [key, value] of new URLSearchParams(location.hash.slice(hashQuery + 1))) {
      merged.set(key, value);
    }
  }
  return Object.fromEntries(merged);
}

/** Only the names a demo build understands, so nothing else in the address reaches the demo server. */
export function readQaSwitches(location: Pick<Location, 'search' | 'hash'>): QaSwitches {
  const params = readQaParams(location);
  const out: Partial<Record<QaSwitchName, string>> = {};
  for (const name of QA_SWITCH_NAMES) {
    const value = params[name];
    if (value !== undefined && value !== '') out[name] = value;
  }
  return out;
}

/** `?demoNow=<ISO instant>` as epoch ms — `@sl/core/demo`'s `parseDemoNow` rule, without the import. */
export function parseDemoNowSwitch(raw: string | undefined): number | null {
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** `?dataset=live` → `live`; anything else (or nothing) is the demo. */
export function parseDatasetSwitch(raw: string | undefined): DatasetChoice {
  return raw === 'live' ? 'live' : 'demo';
}

/** The remembered choice as one string — `demo`, `live`, or `live:<accountKey>` (T-283). */
export function formatDatasetSelection(selection: DatasetSelection): string {
  if (selection.dataset === 'demo') return 'demo';
  return selection.liveAccountKey === null ? 'live' : `live:${selection.liveAccountKey}`;
}

/** The inverse, refusing anything that is not one of the two shapes above. */
export function parseStoredDataset(raw: string | null | undefined): DatasetSelection | null {
  if (!raw) return null;
  if (raw === 'demo') return DEMO_SELECTION;
  if (raw === 'live') return { dataset: 'live', liveAccountKey: null };
  const key = raw.startsWith('live:') ? raw.slice('live:'.length) : null;
  return key !== null && ACCOUNT_KEY.test(key) ? { dataset: 'live', liveAccountKey: key } : null;
}

/**
 * Which account this load is about (T-283).
 *
 * **The address wins whenever it names one**, so a link is always literal: `?dataset=demo` shows the demo even
 * when the last visit chose the live account, which is what makes the switch's own links, the docs' addresses
 * and QA's routes mean what they say. Only when the address is silent does the remembered choice decide — so a
 * reload, and the next morning, stay on the account you picked.
 */
export function parseDatasetSelection(
  params: Readonly<Record<string, string>>,
  stored: DatasetSelection | null,
): DatasetSelection {
  const named = params[DATASET_PARAM];
  if (named === undefined || named === '') return stored ?? DEMO_SELECTION;
  const dataset = parseDatasetSwitch(named);
  if (dataset === 'demo') return DEMO_SELECTION;
  const key = params[LIVE_ACCOUNT_PARAM];
  return {
    dataset: 'live',
    liveAccountKey: key !== undefined && ACCOUNT_KEY.test(key) ? key : null,
  };
}

export interface ReadAppConfigOptions {
  /** The remembered choice, read from `localStorage` by the caller (`app/dataset.ts`) — T-283. */
  storedDataset?: string | null;
  /** The remembered SERVER, read from `localStorage` by the caller (`app/server.ts`) — T-210. */
  storedServer?: string | null;
}

export function readAppConfig(
  env: EnvLike,
  location: Pick<Location, 'search' | 'hash'>,
  options: ReadAppConfigOptions = {},
): AppConfig {
  const apiBuild = env.VITE_API_MODE === 'api';
  const params = readQaParams(location);
  // T-210 — the one choice an API build offers (ARCH §1.2): the backend, or the in-memory demo. The address
  // wins where it names one, exactly as `parseDatasetSelection` does, so a link is literal; otherwise the
  // remembered choice decides. **A DEMO build does not consult either**, which is what makes "nothing a client
  // reads can give this build a backend" structural rather than conventional.
  const server = apiBuild
    ? (parseServerChoice(params[SERVER_PARAM]) ?? parseServerChoice(options.storedServer) ?? 'api')
    : 'demo';
  const transport: Transport = server === 'api' ? 'http' : 'demo';
  const origin = (env.VITE_API_BASE_URL || DEFAULT_API_ORIGIN).replace(/\/+$/, '');
  // An API BUILD has a real server to say what its accounts are, so neither the switch nor the remembered
  // choice applies to it (ARCH §8.3) — and it keeps not applying while that build is showing the demo, because
  // the live look is a facility of a demo build and `?dataset=live` on an API build never meant anything.
  const selection = apiBuild
    ? DEMO_SELECTION
    : parseDatasetSelection(params, parseStoredDataset(options.storedDataset));
  // An `http` build honours no switch at all — by construction, not by convention (ARCH §8.3). **Nor does a
  // LIVE account** (T-283): what the live look shows is what the pull read, and a QA switch that pinned its
  // clock, armed a fault or scripted a scenario would be a fiction laid over Jason's real numbers. The client
  // already passed none of them to the live server; `demoNow` was the one that still leaked, through the clock
  // `main.tsx` builds before the client, which would have made the freshness caption lie.
  //
  // T-210: the gate is the BUILD (`apiBuild`), not the transport in force. An API build showing the demo still
  // honours no switch at all — it is a product build, and TEST_PLAN §1.5 gives the switches to demo builds —
  // so `?demoFault=`, `?demoNow=` and the rest stay inert on it in every state it can be in.
  const qa: QaSwitches = !apiBuild && selection.dataset === 'demo' ? readQaSwitches(location) : {};
  return {
    transport,
    apiBuild,
    ...(apiBuild && env.VITE_PASSWORD_GATE === 'true' ? { passwordGate: true } : {}),
    apiBaseUrl: origin + API_PATH,
    demoNow: parseDemoNowSwitch(qa.demoNow),
    dataset: selection.dataset,
    liveAccountKey: selection.liveAccountKey,
    qa,
    qaKey: QA_SWITCH_NAMES.map((name) => `${name}=${qa[name] ?? ''}`).join('&'),
  };
}
